// `process.env.NODE_ENV` (read by warnIfVisitShapeUnexpected below) is string-
// replaced by Vite at build time (vite.config `define`). Declaring the global
// only here keeps a stray `process.*` in other frontend code tripping no-undef.
/* global process */

/**
 * Tracks whether the current navigation is a bottom-nav tab switch.
 *
 * Tab switches should feel instant (no progress bar, no page-enter animation,
 * no skeleton overlay) — matching native iOS tab bar behavior.
 */

let _isTabSwitch = false;

export function markTabSwitch() {
  _isTabSwitch = true;
}

export function isTabSwitch() {
  return _isTabSwitch;
}

export function clearTabSwitch() {
  _isTabSwitch = false;
}

/**
 * True when an Inertia visit is a background request — a nav-link prefetch or
 * an async partial reload (`router.reload`, polls) — rather than a user-visible
 * navigation. Inertia fires the same global `start`/`finish` events for these
 * as for real navigations, but the page stays rendered and interactive
 * underneath, so they must never drive navigation affordances (progress bar,
 * page skeleton overlay).
 *
 * @param {object} visit - The Inertia visit from a router event's `event.detail`.
 */
export function isBackgroundVisit(visit) {
  return Boolean(visit?.prefetch || visit?.async);
}

let _warnedVisitShape = false;

/**
 * Dev-only tripwire for isBackgroundVisit's coupling to @inertiajs internals.
 *
 * isBackgroundVisit keys off `visit.prefetch` and `visit.async` — required
 * boolean fields on every real Inertia visit, so at least one is always
 * defined today (both `false` for a plain navigation). There is no TypeScript
 * here to catch it if an @inertiajs upgrade renames or drops both: isBackground-
 * Visit would silently return `false` for every visit, and a background prefetch
 * could strand the full-page skeleton again (the regression `f6d543d2` fixed).
 * A unit test can't catch that — it fabricates the visit, so it shares the
 * assumption. So we watch the real visit at runtime instead: warn once, in
 * non-production, when a visit carries neither flag, surfacing the drift in
 * dev/QA at upgrade time rather than silently in production.
 *
 * @param {object} visit - The Inertia visit from a router event's `event.detail`.
 */
export function warnIfVisitShapeUnexpected(visit) {
  if (_warnedVisitShape) return;
  if (process.env.NODE_ENV === 'production') return;
  // Only a present visit that lacks BOTH flags signals drift — a null/undefined
  // visit is just an absent visit, not a renamed field.
  if (!visit || visit.prefetch !== undefined || visit.async !== undefined) {
    return;
  }
  _warnedVisitShape = true;
  console.warn(
    '[nav] Inertia visit has no `prefetch`/`async` flags — isBackgroundVisit ' +
      'is now a no-op, so a background prefetch could strand the page skeleton. ' +
      'Recheck isBackgroundVisit against the latest @inertiajs upgrade.',
  );
}

/**
 * True when an Inertia visit is a mutation that keeps the user on the current
 * page (a settings toggle, an inline action button, etc.) rather than a real
 * navigation. These should never show the full-page loading skeleton or the
 * navigation progress bar — they're not page changes.
 *
 * A visit qualifies when it's non-GET and either preserves component state or
 * targets the same path the user is already on. Real navigations and
 * create→redirect flows (different path, no preserveState) are unaffected.
 *
 * @param {object} visit - The Inertia visit from `router.on('start')`'s event detail.
 */
export function isSamePageMutation(visit) {
  if (!visit || visit.method === 'get') return false;
  const samePath = visit.url?.pathname === window.location.pathname;
  return Boolean(visit.preserveState) || samePath;
}
